import { syncScenarioBranches, type ScenarioLeaderVersion, type ScenarioRowTemplate, type ScenarioBranchBase } from '@agavi/blocks';
import { useEffect, useRef } from 'react';

import type { Block } from '@/types/experience';

/**
 * KEEP A FOLLOWER'S ROWS EQUAL TO THE LEADER'S VARIANTS — §4.1's projection, made continuous.
 *
 * It was applied once, at link time, and that is not what a projection is. Add a fifth scenario
 * to a leader and the follower still held four rows, while its pager — which reads the leader's
 * bank live — listed five. Selecting the fifth found no row and fell back to the first, so the
 * pager appeared to loop, the fifth answer setup could not be reached, and the row that should
 * have been flagged unreviewed did not exist to be flagged. One cause, three symptoms.
 *
 * ## It ADDS and reorders; it does not remove
 *
 * `syncScenarioBranches` drops rows whose variant is gone, which is right once the teacher has
 * been asked (§7.3: "removes the matching slot from every follower — name the followers
 * first"). Until that confirmation exists, a silent drop would destroy an authored answer key
 * because somebody deleted a variant, so orphaned rows are kept on the end instead. They are
 * invisible — the pager is driven by the leader's bank, and every lookup is by id — and §7.3
 * is what will prune them.
 *
 * ## Why an effect and not a derivation
 *
 * The rows are persisted data, so bringing them up to date is a WRITE. Deriving them per render
 * would leave what is stored disagreeing with what is shown — which is the drift the whole
 * feature exists to prevent, reintroduced one level down.
 */
export function useScenarioRowProjection<B extends ScenarioBranchBase>({
  followsBlockId, blocks, rows, template, onSync,
}: {
  followsBlockId: string | undefined;
  blocks: Block[] | undefined;
  rows: B[] | undefined;
  /** What a NEW row starts as — the same copy linking makes. */
  template: () => ScenarioRowTemplate<B>;
  onSync: (next: B[]) => void;
}) {
  const leaderVersions = followsBlockId
    ? ((blocks ?? []).find(b => b.id === followsBlockId)?.data as
      { versions?: ScenarioLeaderVersion[] } | undefined)?.versions
    : undefined;
  /*
   * The identity the effect watches: WHICH variants exist AND WHAT THEY ARE CALLED, not the
   * objects holding them.
   *
   * The name is in here because a rename is now a change the projection has to see — it puts a
   * decided row back to undecided (`ScenarioBranchBase.scenarioName`). Watching ids alone, the
   * detector would be correct and never run: renaming a variant replaces the version object but
   * changes no id, so the effect would not fire until something else moved the bank, and the
   * re-mark would surface at an unrelated moment or not at all.
   */
  const key = (leaderVersions ?? []).map(v => `${v.id}\u0001${v.name ?? ''}`).join('\u0000');
  const have = (rows ?? []).map(r => r.scenarioId).join('\u0000');

  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;
  const templateRef = useRef(template);
  templateRef.current = template;

  useEffect(() => {
    if (!followsBlockId || !leaderVersions?.length) return;
    const { branches, removed } = syncScenarioBranches<B>(leaderVersions, rows, templateRef.current);
    /*
     * AN ORPHAN IS PARKED DATA, NOT PENDING WORK, so it loses the flag on the way out.
     *
     * Reported: a scenario deleted while one follower's row was still unreviewed left that
     * block permanently reading "1 unreviewed" with nothing to review — the row was kept (so
     * the answer key survives a deletion the teacher has not confirmed), but it is unreachable
     * by construction, so the count was asking for an action no control could perform.
     *
     * Clearing the flag rather than the row is the whole point: the authored answer is still
     * there if the variant comes back, and nothing claims it needs attention while it is gone.
     */
    const parked = removed.map(b => (b.unreviewed ? { ...b, unreviewed: undefined } : b));
    const next = [...branches, ...parked];
    // Nothing to do is the common case by far — every render of a linked block reaches here.
    if (next.length === (rows?.length ?? 0)
      && next.every((b, i) => b === rows?.[i])) return;
    onSyncRef.current(next);
    // Only the two identities above: re-running on every data change would re-write the rows
    // out from under live typing, exactly as `useVersionSeeding` records for its own pointer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followsBlockId, key, have]);
}
